import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { AuthService } from '../../auth.service';
import { isApiRequestUrl } from '../utils/api-url.util';

/**
 * Sends API requests with a token that is still valid, refreshing it first if needed: after
 * sleep the background refresh has not run yet. A 401 forces one refresh and one retry.
 * Only requests carrying the session's own token are touched.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const sentToken = req.headers.get('Authorization')?.replace(/^Bearer /, '') ?? '';
  if (!isApiRequestUrl(req.url) || !auth.isSessionToken(sentToken)) {
    return next(req);
  }
  return from(auth.freshAccessToken()).pipe(
    switchMap(token => next(withToken(req, token))),
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
        return throwError(() => error);
      }
      return from(auth.freshAccessToken(true)).pipe(
        switchMap(token => next(withToken(req, token))),
      );
    }),
  );
};

function withToken(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}
